import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./institutionRevenueHistoryCard.module.css";
import {
  getInstitutionRevenueHistory,
  getInstitutionMonthlyRevenue,
} from "../../services/insights";
import { formatNumberToCurrency } from "../../utils/formatter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const YEARS_REQUESTED = 5;

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const InstitutionRevenueHistoryCard = ({ institutionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedYear, setSelectedYear] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState(null);

  const chartRef = useRef(null);

  const loadData = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getInstitutionRevenueHistory({
        institutionId,
        years: YEARS_REQUESTED,
      });
      setData(result);
    } catch (err) {
      setError(err?.message || "Falha ao buscar histórico de receita.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reseta seleção quando muda a instituição
  useEffect(() => {
    setSelectedYear(null);
    setMonthlyData(null);
    setMonthlyError(null);
  }, [institutionId]);

  const fetchMonthly = useCallback(
    async (year) => {
      if (!institutionId || !year) return;
      setMonthlyLoading(true);
      setMonthlyError(null);
      try {
        const result = await getInstitutionMonthlyRevenue({
          institutionId,
          year,
        });
        setMonthlyData(result);
      } catch (err) {
        setMonthlyError(err?.message || "Falha ao buscar receita mensal.");
        setMonthlyData(null);
      } finally {
        setMonthlyLoading(false);
      }
    },
    [institutionId]
  );

  const handleYearSelect = useCallback(
    (year) => {
      if (selectedYear === year) {
        // Toggle: clicar no mesmo ano fecha a tabela
        setSelectedYear(null);
        setMonthlyData(null);
        setMonthlyError(null);
        return;
      }
      setSelectedYear(year);
      fetchMonthly(year);
    },
    [selectedYear, fetchMonthly]
  );

  const handleCloseTable = useCallback(() => {
    setSelectedYear(null);
    setMonthlyData(null);
    setMonthlyError(null);
  }, []);

  if (!institutionId) return null;

  const history = data?.history || [];
  const hasData = history.length > 0;

  const chartData = {
    labels: history.map((item) => String(item.year)),
    datasets: [
      {
        label: "Receita",
        data: history.map((item) => Number(item.revenue) || 0),
        backgroundColor: history.map((item) =>
          item.year === selectedYear ? "#F59E0B" : "#0962AD"
        ),
        borderColor: history.map((item) =>
          item.year === selectedYear ? "#F59E0B" : "#0962AD"
        ),
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 64,
        hoverBackgroundColor: "#F59E0B",
        hoverBorderColor: "#F59E0B",
      },
    ],
  };

  const handleChartClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      false
    );
    if (!elements || elements.length === 0) return;
    const idx = elements[0].index;
    const yearItem = history[idx];
    if (yearItem) {
      handleYearSelect(yearItem.year);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, chartElement) => {
      if (event?.native?.target) {
        event.native.target.style.cursor =
          chartElement.length > 0 ? "pointer" : "default";
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#0962AD",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#F59E0B",
        borderWidth: 1,
        callbacks: {
          label: (context) =>
            `Receita: ${formatNumberToCurrency(context.raw)}`,
          afterBody: () => "Clique para ver receita mês a mês",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#0962AD", font: { size: 13, weight: "700" } },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#293050",
          font: { size: 11 },
          callback: (value) => formatNumberToCurrency(value),
        },
        grid: { color: "rgba(9, 98, 173, 0.08)" },
      },
    },
  };

  const subtitle = hasData
    ? `Período: ${data.startYear} – ${data.endYear}`
    : "Sem dados de receita disponíveis";

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <h3 className={styles.title}>Receita dos últimos {YEARS_REQUESTED} anos</h3>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        {hasData && (
          <div className={styles.totalGroup}>
            <span className={styles.totalLabel}>Total no período</span>
            <span className={styles.totalValue}>
              {formatNumberToCurrency(data.totalRevenue)}
            </span>
          </div>
        )}
      </div>

      {data?.hasInsufficientData && hasData && (
        <div className={styles.warning}>
          <span className={styles.warningIcon}>i</span>
          <span>
            A instituição ainda não possui {YEARS_REQUESTED} anos de histórico.
            Mostrando do ano mais antigo ({data.oldestAvailableYear}) até o mais
            recente ({data.mostRecentAvailableYear}).
          </span>
        </div>
      )}

      {loading && <div className={styles.loading}>Carregando histórico de receita...</div>}
      {error && !loading && <div className={styles.error}>{error}</div>}

      {!loading && !error && !hasData && (
        <div className={styles.empty}>
          Ainda não há dados de receita registrados para esta instituição.
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          <div className={styles.chartWrapper}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              onClick={handleChartClick}
            />
          </div>

          {selectedYear && (
            <div className={styles.monthlyTableWrapper}>
              <div className={styles.monthlyHeader}>
                <div className={styles.monthlyTitleGroup}>
                  <h4 className={styles.monthlyTitle}>
                    Receita mês a mês — {selectedYear}
                  </h4>
                  {monthlyData && (
                    <p className={styles.monthlySubtitle}>
                      Total no ano:{" "}
                      <strong>
                        {formatNumberToCurrency(monthlyData.totalRevenue)}
                      </strong>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={handleCloseTable}
                  aria-label="Fechar tabela"
                  title="Fechar"
                >
                  ×
                </button>
              </div>

              {monthlyLoading && (
                <div className={styles.loading}>Carregando receita mensal...</div>
              )}
              {monthlyError && !monthlyLoading && (
                <div className={styles.error}>{monthlyError}</div>
              )}

              {!monthlyLoading && !monthlyError && monthlyData && (
                <div className={styles.tableWrapper}>
                  <table className={styles.monthlyTable}>
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th className={styles.numericCell}>Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.months.map((m) => (
                        <tr key={m.month}>
                          <td>{MONTH_NAMES[m.month - 1]}</td>
                          <td className={styles.numericCell}>
                            {formatNumberToCurrency(m.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className={styles.numericCell}>
                          {formatNumberToCurrency(monthlyData.totalRevenue)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstitutionRevenueHistoryCard;
